// useUserRole.js
// Hook to determine the current user's role within a specific league
// Roles: 'commissioner' (full access), 'member' (read-only)

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook to fetch and cache the user's role for a specific league.
 * 
 * @param {string} leagueId - The ID of the league to check role for
 * @returns {Object} Role information with helper booleans
 * 
 * @example
 * const { isCommissioner, isMember, loading } = useUserRole(leagueId);
 * 
 * if (loading) return <Spinner />;
 * 
 * if (isCommissioner) {
 *   // Show admin controls
 * }
 */
export function useUserRole(leagueId) {
  const { user } = useAuth();
  const [role, setRole] = useState(null); // 'commissioner' | 'member' | null
  const [isCreator, setIsCreator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset state when dependencies change
    setLoading(true);
    setError(null);
    setRole(null);
    setIsCreator(false);

    // Early return if missing required data
    if (!user) {
      setLoading(false);
      return;
    }

    if (!leagueId) {
      setLoading(false);
      return;
    }

    const fetchRole = async () => {
      try {
        // Fetch user document to get roles
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        // Fetch league to check if user is the creator
        const leagueDocRef = doc(db, 'leagues', leagueId);
        const leagueDocSnap = await getDoc(leagueDocRef);

        if (!leagueDocSnap.exists()) {
          setError('League not found');
          setLoading(false);
          return;
        }

        const leagueData = leagueDocSnap.data();
        const userData = userDocSnap.exists() ? userDocSnap.data() : {};

        // Check if user is the league creator
        const userIsCreator = leagueData?.createdBy === user.uid;
        setIsCreator(userIsCreator);

        // Determine role from user document
        // If user is creator, treat as commissioner regardless of roles object
        const userRoleFromDoc = userData?.roles?.[leagueId];
        
        if (userIsCreator) {
          setRole('commissioner');
        } else if (userRoleFromDoc === 'commissioner') {
          setRole('commissioner');
        } else {
          // Check if user is a member (has the leagueId in their leagues array)
          const isMemberOfLeague = 
            (userData?.leagues || []).includes(leagueId) ||
            userRoleFromDoc !== undefined;
          
          if (isMemberOfLeague) {
            setRole('member');
          } else {
            // User has no role in this league
            setRole(null);
          }
        }
      } catch (err) {
        console.error('Error fetching user role:', err);
        setError(err.message);
        setRole(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [user, leagueId]);

  return {
    // The raw role value
    role,
    
    // Boolean: Is this user the league creator?
    isCreator,
    
    // Boolean: Does this user have commissioner-level access?
    // (either they are the creator OR have commissioner role)
    isCommissioner: role === 'commissioner',
    
    // Boolean: Is this user a regular member (not commissioner)?
    isMember: role === 'member',
    
    // Boolean: Does this user have any access to this league?
    hasAccess: role !== null,
    
    // Loading state
    loading,
    
    // Error state (if any)
    error,
    
    // The authenticated user object
    user,
  };
}

export default useUserRole;
